/**
 * React Query hooks for feed events.
 * React Query hooks for the corresponding entity.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import {
  FEED_EVENT_REJECTION_PERIOD_DAYS,
  GeneralFeed,
  GeneralFeedEvent,
} from "./feedEventTypes";
import { GENERAL_FEED_TYPE, GENERAL_FEED_UNITS } from "./feedStockTypes";
import { pollInsightsRefresh } from "./useInsightsQueries";

// Re-export types for convenience
export {
  FEED_EVENT_REJECTION_PERIOD_DAYS,
  feedEventApi,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
};
export type { GeneralFeed, GeneralFeedEvent };

class FeedAPI extends API<GeneralFeedEvent> {
  async getAllFeedEventsForFarm(
    farmGuid: string,
    feedEventTimeAfter?: string
  ): Promise<GeneralFeedEvent[]> {
    const feedEventTimeAfterParam = feedEventTimeAfter
      ? `&feedEventTimeAfter=${feedEventTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}${this.entity}?farmGuid=${farmGuid}${feedEventTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as GeneralFeedEvent[];
  }
}

const feedEventApi = new FeedAPI("feedEvent");

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all feed events for a farm
 */
export function useFeedEvents(
  farmGuid: string | undefined,
  feedEventTimeAfter?: string
) {
  return useQuery({
    queryKey: [...queryKeys.events.feed.all, farmGuid, feedEventTimeAfter],
    queryFn: () =>
      feedEventApi.getAllFeedEventsForFarm(farmGuid ?? "", feedEventTimeAfter),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single feed event by guid
 */
export function useFeedEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["feedEvent", guid],
    queryFn: () => feedEventApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Create a new feed event
 */
export function useCreateFeedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      generalFeedEvent,
    }: {
      generalFeedEvent: GeneralFeedEvent;
      farmGuid: string;
    }) => {
      const result = await feedEventApi.createOne(generalFeedEvent);
      return { ...generalFeedEvent, ...result };
    },
    onSuccess: async (_, { farmGuid }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(farmGuid),
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}

/**
 * Update an existing feed event
 */
export function useUpdateFeedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      generalFeedEvent,
    }: {
      generalFeedEvent: GeneralFeedEvent;
      farmGuid: string;
    }) => {
      const result = await feedEventApi.updateOne(generalFeedEvent);
      return { ...generalFeedEvent, ...result };
    },
    onSuccess: async (updatedEvent, { farmGuid }) => {
      queryClient.setQueryData(["feedEvent", updatedEvent.guid], updatedEvent);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(farmGuid),
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}

/**
 * Delete a feed event
 */
export function useDeleteFeedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      await feedEventApi.deleteOne(guid);
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["feedEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feed.all,
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}
